import { useNavigate } from "react-router-dom";
import { useState } from "react";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";
import axios from "axios";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const TablesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const TopTablesRow = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const InfoTileHeader = styled.div<{ $color?: string }>`
  width: 100%;
  background: ${(props) => props.$color || "#f8f9fa"};
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: white;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const InfoTileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const FormInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 0.75rem 2rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface Props {
  setSnackbar: any;
}

const CreateVendor: React.FC<Props> = ({ setSnackbar }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Company Details state
  const [status, setStatus] = useState("Active");
  const [source, setSource] = useState("Direct");
  const [companyName, setCompanyName] = useState("");
  const [dba, setDba] = useState("");
  const [address, setAddress] = useState("");
  const [suite, setSuite] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const token = localStorage.getItem("token");

  // Payment Details state
  const [paymentTerms, setPaymentTerms] = useState("");
  const [paymentType, setPaymentType] = useState("ACH");
  const [accountRepresentative, setAccountRepresentative] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [billingEmail, setBillingEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        API_ENDPOINTS.VENDORS,
        {
          company_name: companyName,
          source,
          status: status === "Active" ? 1 : 0,
          address,
          suite,
          city,
          state,
          zip,
          payment_terms: paymentTerms,
          payment_type: paymentType.toLowerCase(),
          payment_representative: accountRepresentative,
          account_email: accountEmail,
          billing_email: billingEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackbar({
        isOpen: true,
        message: "Vendor created successfully",
        type: "success",
      });
      navigate("/vendors");
    } catch (e: any) {
      console.error("Error creating vendor:", e);
      setSnackbar({
        isOpen: true,
        message: "Failed to create vendor. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/vendors");
  };

  return (
    <Container>
      <PageTitle>Create Vendor</PageTitle>
      <GoBack onClick={() => navigate("/vendors")}>← Back to Vendors</GoBack>

      <form onSubmit={handleSubmit}>
        <TablesContainer>
          <TopTablesRow>
            {/* Company Details Table */}
            <InfoTile>
              <InfoTileHeader $color="#6B8E6B">
                <h2>Company Details</h2>
              </InfoTileHeader>
              <InfoTileSection>
                <FormGroup>
                  <FormLabel htmlFor="status">Status</FormLabel>
                  <FormSelect
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="source">Source</FormLabel>
                  <FormSelect
                    id="source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    required
                  >
                    <option value="Direct">Direct</option>
                    <option value="Host & Post">Host & Post</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="companyName">Company Name</FormLabel>
                  <FormInput
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="dba">DBA</FormLabel>
                  <FormInput
                    type="text"
                    id="dba"
                    value={dba}
                    onChange={(e) => setDba(e.target.value)}
                    placeholder="Enter DBA"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="address">Address</FormLabel>
                  <FormInput
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter address"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="suite">Suite</FormLabel>
                  <FormInput
                    type="text"
                    id="suite"
                    value={suite}
                    onChange={(e) => setSuite(e.target.value)}
                    placeholder="Enter suite"
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="city">City</FormLabel>
                  <FormInput
                    type="text"
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter city"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="state">State</FormLabel>
                  <FormInput
                    type="text"
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="Enter state"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="zip">Zip</FormLabel>
                  <FormInput
                    type="text"
                    id="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="Enter zip code"
                    required
                  />
                </FormGroup>
              </InfoTileSection>
            </InfoTile>

            {/* Payment Details Table */}
            <InfoTile>
              <InfoTileHeader $color="#5A7A9A">
                <h2>Payment Details</h2>
              </InfoTileHeader>
              <InfoTileSection>
                <FormGroup>
                  <FormLabel htmlFor="paymentTerms">Payment Terms</FormLabel>
                  <FormInput
                    type="text"
                    id="paymentTerms"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="Enter payment terms"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="paymentType">Payment Type</FormLabel>
                  <FormSelect
                    id="paymentType"
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    required
                  >
                    <option value="ACH">ACH</option>
                    <option value="Credit">Credit</option>
                    <option value="Prepay">Prepay</option>
                    <option value="Paypal">Paypal</option>
                    <option value="Wire">Wire</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="accountRepresentative">
                    Account Representative
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="accountRepresentative"
                    value={accountRepresentative}
                    onChange={(e) => setAccountRepresentative(e.target.value)}
                    placeholder="Enter account representative"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="accountEmail">Account Email</FormLabel>
                  <FormInput
                    type="email"
                    id="accountEmail"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    placeholder="Enter account email"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="billingEmail">Billing Email</FormLabel>
                  <FormInput
                    type="email"
                    id="billingEmail"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="Enter billing email"
                    required
                  />
                </FormGroup>
              </InfoTileSection>
            </InfoTile>
          </TopTablesRow>
        </TablesContainer>

        <ButtonGroup>
          <Button
            type="button"
            $variant="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Vendor"}
          </Button>
        </ButtonGroup>
      </form>
    </Container>
  );
};

export default CreateVendor;
